const playbackBySection = new Map();

class OneHunnaCampaignVideo extends HTMLElement {
  #video;
  #soundButton;
  #playback;
  #events;
  #observer;
  #viewport;
  #playButton;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #visible = false;
  #playPending = false;
  #playInterrupted = false;
  #restoring = false;
  #pageHidden = false;
  #frameCallback;

  connectedCallback() {
    if (this.#events) return;
    this.#viewport = window.matchMedia(this.dataset.mediaQuery || '(min-width: 0px)');
    const placeholder = this.querySelector('video');
    if (!placeholder?.dataset.src) return;

    const key = this.dataset.playbackKey + ':' + this.dataset.autoplay + ':' + this.dataset.once;
    const previous = playbackBySection.get(key);
    if (previous?.video.dataset.src === placeholder.dataset.src && !previous.video.isConnected) {
      if (previous.video.poster !== placeholder.poster) previous.video.poster = placeholder.poster;
      placeholder.replaceWith(previous.video);
      this.#playback = previous;
    } else if (previous?.video === placeholder) {
      this.#playback = previous;
    } else {
      placeholder.muted = true;
      this.#playback = { video: placeholder, time: 0, completed: false, blocked: false, frameTime: null, frameInterval: 0 };
      playbackBySection.set(key, this.#playback);
    }

    this.#video = this.#playback.video;
    this.#video.loop = false;
    this.#video.removeAttribute('loop');
    this.#video.playsInline = true;
    this.#video.autoplay = false;
    this.#visible = false;
    this.#pageHidden = false;
    this.#restoring = false;
    this.#events = new AbortController();
    const options = { signal: this.#events.signal };

    this.#playButton = this.querySelector('.hunna-video-play');
    if (this.#playButton) {
      this.#playButton.addEventListener('click', this.#togglePlay, options);
      this.#playButton.hidden = false;
    }
    this.#soundButton = this.querySelector('.hunna-video-sound');
    if (this.#soundButton) {
      this.#soundButton.addEventListener('click', this.#toggleSound, options);
      this.#syncSoundButton();
      this.#soundButton.hidden = false;
    }
    this.#video.addEventListener('volumechange', this.#syncSoundButton, options);
    this.#video.addEventListener('loadedmetadata', this.#restorePosition, options);
    this.#video.addEventListener('canplaythrough', this.#update, options);
    this.#video.addEventListener('seeked', this.#onSeeked, options);
    this.#video.addEventListener('seeking', this.#guardCompleted, options);
    this.#video.addEventListener('play', this.#onPlay, options);
    this.#video.addEventListener('pause', this.#onPause, options);
    this.#video.addEventListener('ended', this.#onEnded, options);
    document.addEventListener('visibilitychange', this.#update, options);
    window.addEventListener('pagehide', this.#onPageHide, options);
    window.addEventListener('pageshow', this.#onPageShow, options);
    this.#viewport.addEventListener('change', this.#update, options);
    this.#reducedMotion.addEventListener('change', this.#update, options);

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      this.#update();
    });
    this.#observer.observe(this);
    this.#restorePosition();
  }

  disconnectedCallback() {
    if (!this.#events) return;
    this.#rememberPosition();
    this.#events.abort();
    this.#events = null;
    this.#playPending = false;
    this.#observer.disconnect();
    this.#stopFrameTracking();
    this.#video.autoplay = false;
    this.#video.pause();
  }

  #canPlay = () =>
    this.isConnected && this.#viewport.matches && this.#visible &&
    !document.hidden && !this.#pageHidden && (!this.#reducedMotion.matches || this.#playback.userStarted) &&
    !this.#playback.userPaused && (this.dataset.autoplay !== 'false' || this.#playback.userStarted) &&
    !this.#playback.completed && !this.#playback.blocked && !this.#restoring;

  #update = () => {
    if (!this.#events) return;
    this.#syncSoundButton();
    if (this.#playback.completed) {
      this.#holdFinalFrame();
      return;
    }
    if (!this.#canPlay()) {
      if (this.#playPending) this.#playInterrupted = true;
      this.#video.autoplay = false;
      this.#video.pause();
      this.#syncSoundButton();
      return;
    }
    if (this.#playPending || !this.#video.paused) return;

    if (!this.#video.hasAttribute('src')) {
      this.#video.preload = 'auto';
      this.#video.autoplay = false;
      this.#video.src = this.#video.dataset.src;
    }
    // Let the browser buffer enough for continuous playback before automatic start.
    // A deliberate play/sound gesture remains immediate, including on data-saving browsers.
    if (!this.#playback.userStarted && this.#video.readyState < HTMLMediaElement.HAVE_ENOUGH_DATA) return;
    // This controller alone starts playback. Native autoplay can force an
    // additional central iOS control when low-power mode blocks autoplay.
    this.#video.autoplay = false;
    this.#playPending = true;
    this.#playInterrupted = false;
    const signal = this.#events.signal;
    const play = this.#video.play();
    // paused can change before the browser dispatches its queued play event.
    this.#syncSoundButton();
    play.catch((error) => {
      if (signal.aborted) return;
      if (error.name !== 'AbortError') {
        this.#playback.blocked = true;
        this.#video.autoplay = false;
        this.#video.pause();
      }
    }).finally(() => {
      if (signal.aborted) return;
      this.#playPending = false;
      // A rejected play() must leave the control in the actual paused state.
      this.#syncSoundButton();
      if (this.#playInterrupted && this.#canPlay()) this.#update();
    });
  };

  #togglePlay = () => {
    if (this.#playback.completed && this.dataset.once !== 'false') return;
    if (!this.#video.paused) {
      this.#playback.userPaused = true;
      this.#video.pause();
    } else {
      this.#playback.userStarted = true;
      this.#playback.userPaused = false;
      this.#playback.blocked = false;
      if (this.#playback.completed) {
        this.#playback.completed = false;
        this.#playback.time = 0;
        this.#playback.frameTime = null;
        this.#playback.frameInterval = 0;
        this.#video.currentTime = 0;
        this.#observer.observe(this);
      }
      this.#update();
    }
    this.#syncSoundButton();
  };

  #toggleSound = () => {
    if (this.#playback.completed) return;
    this.#video.muted = !this.#video.muted;
    this.#playback.userStarted = true;
    this.#playback.blocked = false;
    this.#update();
  };

  #syncSoundButton = () => {
    if (this.#soundButton) {
      const pressed = String(!this.#video.muted);
      if (this.#soundButton.getAttribute('aria-pressed') !== pressed) this.#soundButton.setAttribute('aria-pressed', pressed);
      this.#soundButton.disabled = this.#playback.completed;
    }
    if (this.#playButton) {
      const finished = this.#playback.completed && this.dataset.once !== 'false';
      const label = finished ? this.#playButton.dataset.endedLabel : this.#video.paused ? this.#playButton.dataset.playLabel : this.#playButton.dataset.pauseLabel;
      this.#playButton.disabled = finished;
      this.#playButton.setAttribute('aria-label', label);
      this.#playButton.querySelector('.visually-hidden').textContent = label;
      const state = finished ? 'ended' : this.#playback.completed ? 'replay' : this.#video.paused ? 'play' : 'pause';
      // Keep the real SVG geometry intact in every playback state.
      this.#playButton.querySelectorAll('[data-video-icon]').forEach((icon) => {
        icon.toggleAttribute('hidden', icon.dataset.videoIcon !== state);
      });
    }
  };

  #rememberPosition = () => {
    if (!this.#restoring && !this.#playback.completed) {
      this.#playback.time = this.#video.currentTime;
    }
  };

  #restorePosition = () => {
    if (this.#playback.completed) {
      this.#holdFinalFrame();
      return;
    }
    const { duration, currentTime } = this.#video;
    if (Number.isFinite(duration) && this.#playback.time > 0 &&
        Math.abs(currentTime - this.#playback.time) > 0.05) {
      this.#restoring = true;
      this.#video.currentTime = Math.min(this.#playback.time, Math.max(0, duration - 0.001));
    }
  };

  #onSeeked = () => {
    this.#restoring = false;
    this.#update();
  };

  #onPlay = () => {
    this.#syncSoundButton();
    if (this.#playback.completed) this.#holdFinalFrame();
    else if (!this.#canPlay()) this.#video.pause();
    else if (this.#video.requestVideoFrameCallback && this.#frameCallback == null) {
      this.#frameCallback = this.#video.requestVideoFrameCallback(this.#onFrame);
    }
  };

  #onFrame = (_, { mediaTime }) => {
    const interval = mediaTime - this.#playback.frameTime;
    if (this.#playback.frameTime != null && interval > 0 && interval < 0.25) {
      this.#playback.frameInterval = this.#playback.frameInterval
        ? Math.min(this.#playback.frameInterval, interval)
        : interval;
    }
    this.#playback.frameTime = mediaTime;
    this.#frameCallback = null;
    if (!this.#video.paused && !this.#playback.completed) {
      this.#frameCallback = this.#video.requestVideoFrameCallback(this.#onFrame);
    }
  };

  #stopFrameTracking = () => {
    if (this.#frameCallback != null) this.#video.cancelVideoFrameCallback(this.#frameCallback);
    this.#frameCallback = null;
  };

  #onPause = () => {
    this.#syncSoundButton();
    this.#rememberPosition();
    this.#stopFrameTracking();
  };

  #onEnded = () => {
    if (this.#playback.completed) return;
    this.#playback.completed = true;
    this.#syncSoundButton();
    this.#playback.time = this.#video.currentTime;
    const { frameTime, frameInterval } = this.#playback;
    this.#playback.needsTerminalSeek = frameTime == null ||
      (frameInterval > 0 && this.#video.duration - frameTime > frameInterval * 1.5);
    this.#observer.disconnect();
    this.#holdFinalFrame();
  };

  #guardCompleted = () => {
    if (this.#playback.completed) this.#holdFinalFrame();
  };

  #holdFinalFrame = () => {
    this.#video.autoplay = false;
    this.#video.pause();
    const { duration, currentTime } = this.#video;
    if (!this.#restoring && Number.isFinite(duration) && duration > 0 &&
        (this.#playback.needsTerminalSeek || currentTime < duration - 0.0015)) {
      this.#playback.needsTerminalSeek = false;
      this.#restoring = true;
      this.#video.currentTime = Math.max(0, duration - 0.001);
    }
  };

  #onPageHide = () => {
    this.#pageHidden = true;
    this.#update();
  };

  #onPageShow = () => {
    this.#pageHidden = false;
    this.#update();
  };
}

if (!customElements.get('one-hunna-campaign-video')) {
  customElements.define('one-hunna-campaign-video', OneHunnaCampaignVideo);
}
